import React, { useState, useEffect } from 'react';
import './Contact.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEnvelope, 
  faPhone, 
  faPaperPlane,
  faSpinner 
} from '@fortawesome/free-solid-svg-icons';
import emailjs from '@emailjs/browser';
import DOMPurify from 'dompurify';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [csrfToken, setCsrfToken] = useState('');

  useEffect(() => {
    const newToken = Math.random().toString(36).substring(7);
    setCsrfToken(newToken);
    sessionStorage.setItem('csrfToken', newToken);
  }, []);

  const handleChange = (e) => {
    const sanitizedValue = DOMPurify.sanitize(e.target.value);
    setFormData({
      ...formData,
      [e.target.name]: sanitizedValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const storedToken = sessionStorage.getItem('csrfToken');
    if (csrfToken !== storedToken) {
      setStatus('error');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setStatus('error');
      return;
    }

    setLoading(true);
    setStatus('');

    // Ajout d'un timestamp pour le rate limiting
    const lastSubmission = localStorage.getItem('lastSubmissionTime');
    const currentTime = Date.now();
    
    if (lastSubmission && currentTime - parseInt(lastSubmission) < 60000) { // 1 minute
      setStatus('rate-limited');
      setLoading(false);
      return;
    }

    try {
      const templateParams = {
        from_name: DOMPurify.sanitize(formData.name),
        from_email: DOMPurify.sanitize(formData.email),
        subject: DOMPurify.sanitize(formData.subject),
        message: DOMPurify.sanitize(formData.message),
        security_token: process.env.REACT_APP_EMAILJS_SECURITY_TOKEN,
        csrf_token: csrfToken
      };

      localStorage.setItem('lastSubmissionTime', currentTime.toString());
      
      await emailjs.send(
        'service_67b6tbd',
        'template_4f5ysdt',
        templateParams,
        'pMoIGFLGTzpE9XM4o'
      );

      // Réinitialisation du formulaire
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setStatus('success');
    } catch (error) {
      setStatus('error');
      console.error('Erreur lors de l\'envoi:', error);
    }

    setLoading(false);
  };

  return (
    <div className="contact">
      <div className="contact-container">
        <div className="contact-info">
          <h2>Contactez-moi</h2>
          <p>N'hésitez pas à me contacter pour toute question ou demande de devis.</p>
          
          <div className="info-items">
            <div className="info-item">
              <FontAwesomeIcon icon={faEnvelope} />
              <div>
                <h3>Email</h3>
                <p><a href="mailto:judcoco19@gmail.com">judcoco19@gmail.com</a></p>
              </div>
            </div>

            <div className="info-item">
              <FontAwesomeIcon icon={faPhone} />
              <div>
                <h3>Discord</h3>
                <p>Judcoco#9853</p>
              </div>
            </div>
          </div>

          <div className="contact-availability">
            <h3>Disponibilité</h3>
            <p>Lundi - Vendredi : les soirs</p>
            <p>Réponse sous 24-48h</p>
          </div>
        </div>

        <form className="contact-form" onSubmit={handleSubmit}>
          <input 
            type="hidden" 
            name="csrf_token" 
            value={csrfToken} 
          />

          <div className="form-group">
            <label htmlFor="name">Nom complet *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Votre nom"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="votre@email.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="subject">Sujet</label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="Sujet de votre message"
            />
          </div>

          <div className="form-group">
            <label htmlFor="message">Message *</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              placeholder="Votre message..."
              rows="6"
            />
          </div>

          <button 
            type="submit" 
            className="submit-button"
            disabled={loading}
          >
            {loading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <>
                <span>Envoyer</span>
                <FontAwesomeIcon icon={faPaperPlane} />
              </>
            )}
          </button>

          {status === 'success' && (
            <div className="alert success">
              Message envoyé avec succès !
            </div>
          )}

          {status === 'error' && (
            <div className="alert error">
              Une erreur est survenue. Veuillez réessayer.
            </div>
          )}

          {status === 'rate-limited' && (
            <div className="alert error">
              Veuillez attendre une minute avant de renvoyer un message.
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default Contact;